// Playback speed and audio boost formatting
// (lib/utils/playback_speed_utils.dart, lib/utils/audio_boost_utils.dart).

export const MIN_PLAYBACK_SPEED = 0.5;
export const MAX_PLAYBACK_SPEED = 3.0;
export const PLAYBACK_SPEED_STEP = 0.05;

/** Dart `num.round()`: halves round away from zero. */
const dartRound = (value: number): number => {
  const rounded = Math.sign(value) * Math.round(Math.abs(value));
  return rounded === 0 ? 0 : rounded;
};

/** Clamps to 0.5..3.0, snaps to 0.05 steps and rounds to 2 decimals. */
export function normalizePlaybackSpeed(speed: number): number {
  const value = Number.isFinite(speed) ? speed : 1;
  const clamped = Math.min(MAX_PLAYBACK_SPEED, Math.max(MIN_PLAYBACK_SPEED, value));
  const stepped = dartRound(clamped / PLAYBACK_SPEED_STEP) * PLAYBACK_SPEED_STEP;
  return Number.parseFloat(stepped.toFixed(2));
}

/** 1 → "1x", 1.25 → "1.25x", 1.5 → "1.5x", 0.75 → "0.75x". */
export function formatPlaybackSpeed(speed: number): string {
  const fixed = normalizePlaybackSpeed(speed).toFixed(2);
  return `${fixed.replace(/0+$/, '').replace(/\.$/, '')}x`;
}

export function playbackSpeedsEqual(a: number, b: number, tolerance = 0.001): boolean {
  return Math.abs(a - b) < tolerance;
}

export const MIN_AUDIO_BOOST = 0;
export const DEFAULT_AUDIO_BOOST = 1;
export const MAX_AUDIO_BOOST = 2;

export function normalizeAudioBoost(value: number): number {
  const v = Number.isFinite(value) ? value : DEFAULT_AUDIO_BOOST;
  return Math.min(MAX_AUDIO_BOOST, Math.max(MIN_AUDIO_BOOST, v));
}

/** 1.0 → "0%", 1.25 → "+25%", 0.5 → "-50%", 2.0 → "+100%". */
export function formatAudioBoostPercent(value: number): string {
  const delta = dartRound((normalizeAudioBoost(value) - DEFAULT_AUDIO_BOOST) * 100);
  return delta > 0 ? `+${delta}%` : `${delta}%`;
}
